import React from "react";
import Stages from "../stages/Stages";
import { useSelector, useDispatch } from "react-redux";
import {
  addGoal,
  reorderStagesBetweenGoals,
  reorderStagesWithinGoals,
  reorderUserActionsBetweenStages,
  reorderUserActionsWithinStages,
  selectGoals,
  selectStages,
} from "../../store/coreSlice";
import TitleBanner from "../../components/TitleBanner/TitleBanner";
import "./Goals.scss";
import AddButtonComponent from "../../components/AddButtonComponent/AddButtonComponent";
import { DragDropContext, Droppable } from "react-beautiful-dnd";
import ScrollContainer from "react-indiana-drag-scroll";

const reorderEntities = (
  sourceEntity,
  startIndex,
  endIndex,
  targetEntityKey
) => {
  const targetEntity = sourceEntity[targetEntityKey];
  const [removed] = targetEntity.splice(startIndex, 1);
  targetEntity.splice(endIndex, 0, removed);

  const newStage = {
    ...sourceEntity,
    targetEntity,
  };

  return newStage;
};

export default function Goals() {
  const goals = useSelector(selectGoals);
  const stages = useSelector(selectStages);
  const dispatch = useDispatch();

  const handleAddGoalClick = (index) => {
    dispatch(addGoal({appendAfterIndex: index}));
  };

  const onDragEnd = (result) => {
    const { destination, source, type } = result;

    // If user tries to drop in an unknown destination
    if (!destination) return;

    // If the user drags and drops back in the same position
    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    ) {
      return;
    }

    if (type === "User_Action") {
      const sourceStageIndex = stages.findIndex(
        (d) => d.id === source.droppableId
      );
      const sourceStage = JSON.parse(JSON.stringify(stages[sourceStageIndex]));
      const destinationStageIndex = stages.findIndex(
        (d) => d.id === destination.droppableId
      );
      const destinationStage = JSON.parse(
        JSON.stringify(stages[destinationStageIndex])
      );

      // If the user drops within the same column but in a different position
      if (sourceStage.id === destinationStage.id) {
        const newColumn = reorderEntities(
          sourceStage,
          source.index,
          destination.index,
          "userActions"
        );

        dispatch(
          reorderUserActionsWithinStages({
            stageId: sourceStage.id,
            updatedStage: newColumn,
          })
        );
        return;
      }

      // If the user moves from one column to another
      const startUserActions = sourceStage.userActions;
      const [removed] = startUserActions.splice(source.index, 1);
      const newStartCol = {
        ...sourceStage,
        userActions: startUserActions,
      };

      const endUserActions = destinationStage.userActions;
      removed.parentId = destinationStage.id;
      endUserActions.splice(destination.index, 0, removed);
      const newEndCol = {
        ...destinationStage,
        userActions: endUserActions,
      };

      dispatch(
        reorderUserActionsBetweenStages({
          sourceStageId: newStartCol.id,
          updatedSourceStage: newStartCol,
          targetStageId: newEndCol.id,
          targetStage: newEndCol,
        })
      );
    } else if (type === "STAGE_LIST") {
      const sourceGoalIndex = goals.findIndex(
        (d) => d.id === source.droppableId
      );
      const sourceGoal = JSON.parse(JSON.stringify(goals[sourceGoalIndex]));
      const destinationGoalIndex = goals.findIndex(
        (d) => d.id === destination.droppableId
      );
      const destinationGoal = JSON.parse(
        JSON.stringify(goals[destinationGoalIndex])
      );

      // If the user drops within the same Goal but in a different position
      if (sourceGoal.id === destinationGoal.id) {
        const newGoal = reorderEntities(
          sourceGoal,
          source.index,
          destination.index,
          "stages"
        );

        dispatch(
          reorderStagesWithinGoals({
            goalId: sourceGoal.id,
            updatedGoal: newGoal,
          })
        );
        return;
      }

      // If the user moves the stage from one goal to another goal
      const startStages = sourceGoal.stages;
      const [removedStage] = startStages.splice(source.index, 1);
      const newStartGoal = {
        ...sourceGoal,
        stages: startStages,
      };

      const endStages = destinationGoal.stages;
      endStages.splice(destination.index, 0, removedStage);
      const newEndGoal = {
        ...destinationGoal,
        stages: endStages,
      };

      dispatch(
        reorderStagesBetweenGoals({
          sourceGoalId: newStartGoal.id,
          targetGoalId: newEndGoal.id,
          sourceGoal: newStartGoal,
          targetGoal: newEndGoal,
        })
      );
    }
  };

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <ScrollContainer
        ignoreElements=".draggable-entity"
        className="goals-container"
        hideScrollbars={false}
        activationDistance={0}
      >
        {goals.map((eachGoal, index) => (
          <article key={eachGoal.id} className="goal-wrapper">
            <section className="goal-content">
              <TitleBanner
                enableAutoFocus={true}
                placeholder="Goal of Major Stage"
                handleOnEnterPress={handleAddGoalClick.bind(this, index)}
              />
              <div style={{ margin: "1.33rem 0 1.33rem 0.7rem" }}>
                <AddButtonComponent
                  title="Add Goal"
                  handleAddButtonClick={handleAddGoalClick.bind(this, index)}
                />
              </div>
            </section>

            <Droppable
              key={`goal-${eachGoal.id}`}
              droppableId={`${eachGoal.id}`}
              direction="horizontal"
              type="STAGE_LIST"
            >
              {(droppableProvided, droppableSnapshot) => (
                <section
                  className="stage-list"
                  ref={droppableProvided.innerRef}
                  {...droppableProvided.droppableProps}
                >
                  <Stages
                    key={eachGoal.id}
                    goalId={eachGoal.id}
                    stageIds={eachGoal.stages}
                  />
                  {droppableProvided.placeholder}
                </section>
              )}
            </Droppable>
          </article>
        ))}
      </ScrollContainer>
    </DragDropContext>
  );
}
